import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GlobalContext } from "../../Context/GlobalContext";
import { toast } from "react-hot-toast";
import {
  IndianRupee,
  BarChart3,
  Filter,
  Search,
  Building2,
  ChevronRight,
  Info,
} from "lucide-react";

import useBilling from "../Services/BillingServices/useBilling";
/**
 * AdminBilling (New Layout)
 * - Fetches client billing overview for this admin
 * - Filters (search + status), sleek table with green header
 * - Actions:
 *   • View Insights  -> /admin/billing/insights/:clientId/:clientName
 *   • View Billing   -> /admin/billing/bill/:clientId/:clientName
 *
 * Endpoint used:
 * POST /Admin/v1.0/getUserBillingData { userid: adminId }
 * Response (expected): [{ id, user_name, billingAmount, billingStatus, updatedOn }]
 */

export default function AdminBilling() {
  const {getUserBillingData, }=useBilling();

  const { api, adminId } = useContext(GlobalContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(""); // "", "Paid", "Pending", "Overdue"

  useEffect(() => {
    const fetchClients = async () => {
      if (!adminId) return;

      setLoading(true);

      try {
        const result =
          await getUserBillingData(adminId);

        if (result.success) {
          setClients(result.data);
        } else {
          setClients([]);
          toast.error(result.message);
        }
      } catch {
        setClients([]);
        toast.error("Error fetching clients");
      } finally {
        setLoading(false);
      }
    };

    fetchClients();
  }, [adminId]);


  const filtered = useMemo(() => {
    return (clients || []).filter((c) => {
      const s = search.trim().toLowerCase();
      const matchesSearch =
        !s ||
        (c.user_name || "").toLowerCase().includes(s) ||
        (c.project || "").toLowerCase().includes(s);
      const matchesStatus = !status || (c.billingStatus || "") === status;
      return matchesSearch && matchesStatus;
    });
  }, [clients, search, status]);

  const badgeStyle = (s) =>
    s === "Paid"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : s === "Pending"
      ? "bg-amber-50 text-amber-700 border-amber-200"
      : "bg-rose-50 text-rose-700 border-rose-200";

  return (
    <div className="p-4">
      {/* Heading */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-xl bg-[#01C37D]/10 text-[#01C37D]">
            <IndianRupee size={20} />
          </div>
          <div>
            <div className="text-lg font-bold text-gray-900">Billing</div>
            <div className="text-xs text-gray-500">
              Overview of client billing & quick navigation to details.
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border bg-white p-2">
        <div className="flex items-center gap-2">
          <div className="grid h-9 w-9 place-items-center rounded-lg bg-gray-100 text-gray-700">
            <Search size={16} />
          </div>
          <input
            className="h-9 w-[260px] rounded-lg border px-3 text-sm outline-none"
            placeholder="Search clients or projects…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="ml-auto flex items-center gap-2">
          <div className="grid h-9 w-9 place-items-center rounded-lg bg-gray-100 text-gray-700">
            <Filter size={16} />
          </div>
          <select
            className="h-9 rounded-lg border px-2 text-sm outline-none"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="Paid">Paid</option>
            <option value="Pending">Pending</option>
            <option value="Overdue">Overdue</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border bg-white">
        {/* header */}
        <div className="grid grid-cols-12 gap-2 border-b bg-[#01C37D] px-3 py-2 text-xs font-semibold text-white">
          <div className="col-span-5">Client</div>
          <div className="col-span-2 text-right">Total Billing</div>
          <div className="col-span-2 text-center">Status</div>
          <div className="col-span-2 text-center">Updated</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>

        {loading ? (
          <div className="py-14 text-center text-gray-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="flex items-center justify-center gap-2 py-14 text-gray-400">
            <Info size={16} /> No records found
          </div>
        ) : (
          filtered.map((c) => (
            <div
              key={c.id}
              className="grid grid-cols-12 items-center gap-2 px-3 py-2 text-sm"
            >
              <div className="col-span-5 flex items-center gap-2">
                <div className="grid h-8 w-8 place-items-center rounded-md bg-gray-100 text-gray-700">
                  <Building2 size={16} />
                </div>
                <div className="font-medium">{c.user_name}</div>
              </div>

              <div className="col-span-2 text-right font-semibold">
                ₹ {Number(c.billingAmount || 0).toFixed(2)}
              </div>

              <div className="col-span-2 text-center">
                <span
                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold ${badgeStyle(
                    c.billingStatus
                  )}`}
                >
                  {c.billingStatus}
                </span>
              </div>

              <div className="col-span-2 text-center text-gray-600">
                {c.updatedOn || "—"}
              </div>

              <div className="col-span-1 flex justify-end">
                <div className="flex items-center gap-2">
                  <button
                    className="inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs hover:bg-gray-50"
                    title="Insights"
                    onClick={() =>
                      navigate(
                        `/admin/billing/insights/${c.id}/${encodeURIComponent(
                          c.user_name
                        )}`
                      )
                    }
                  >
                    <BarChart3 size={14} />
                    Insights
                  </button>
                  <button
                    className="inline-flex items-center gap-1 rounded-full bg-[#01C37D] px-3 py-1 text-xs font-semibold text-white"
                    title="Billing"
                    onClick={() =>
                      navigate(
                        `/admin/billing/bill/${c.id}/${encodeURIComponent(
                          c.user_name
                        )}`
                      )
                    }
                  >
                    <IndianRupee size={14} />
                    Bill
                    <ChevronRight size={14} className="ml-0.5" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
